import Enums from '../types/api/Enums';
import { NewQuoteStatus, type QuoteStatus } from '../types/api/QuoteStatus/QuoteStatus';
import type { QuoteSummary } from '../types/api/QuoteSummary/QuoteSummary';

export const generateAutoProjectName = (quoteName: string, locale: string): string => {
  let dateSuffix: string;
  try {
    dateSuffix = new Date().toLocaleDateString(locale, {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  } catch {
    dateSuffix = new Date().toLocaleDateString('en-US', {
      month: 'short',
      day: 'numeric',
      year: 'numeric',
    });
  }
  return quoteName ? `${quoteName} - ${dateSuffix}` : dateSuffix;
};

type canEditCurrentRevisionProps = {
  requiresQuoteApprovals: boolean;
  savedQuoteStatus: QuoteStatus | undefined;
};

/**
 * CanEditCurrentRevision returns true if the user can save changes to this
 * Quote, and false if they must "save as new" to create a new Quote revision
 * upon making (non-status) changes.
 *
 * A user can edit a quote if there are no quote policies or quote approvals
 * are not enabled, or if the quote's status has isDraft=true.
 */

export const canEditCurrentRevision = ({
  requiresQuoteApprovals,
  savedQuoteStatus,
}: canEditCurrentRevisionProps): boolean =>
  !requiresQuoteApprovals ||
  savedQuoteStatus === undefined ||
  savedQuoteStatus.isDraft() ||
  savedQuoteStatus.isApprovalRequest();

const summaryStatusBehaviors: Array<[keyof QuoteSummary, Enums.QuoteStatusSystemBehavior]> = [
  ['isDraft', Enums.QuoteStatusSystemBehavior.Draft],
  ['isApprovalRequest', Enums.QuoteStatusSystemBehavior.ApprovalRequest],
  ['isRejected', Enums.QuoteStatusSystemBehavior.ApproverRejected],
  ['isApprovedToSend', Enums.QuoteStatusSystemBehavior.ApprovedToSend],
  ['isSent', Enums.QuoteStatusSystemBehavior.Sent],
];

/**
 * quoteStatusFromSummary reconstructs a QuoteStatus from the boolean status
 * flags carried on a QuoteSummary, so list rows (which only have the summary,
 * not the full status object) can use the shared QuoteStatus helpers.
 */
export const quoteStatusFromSummary = (summary: QuoteSummary): QuoteStatus => {
  const match = summaryStatusBehaviors.find(([flag]) => summary[flag]);

  return NewQuoteStatus({
    id: summary.statusId,
    name: summary.statusName,
    systemBehavior: match ? match[1] : Enums.QuoteStatusSystemBehavior.Sent,
  });
};
